import React, { useState } from 'react';
import { css, useTheme} from "@emotion/react";

import {
  dropShadows,
  mediaQueries, 
  pxToRem, 
  transition, 
  z,
  weights,
  fonts,
  fontSizeLineHeight,
} from '../styles';

import { UpsunTheme } from '../themes';

import { ReactComponent as ChevronUp } from "../icons/chevron-up-small.svg";

import { 
  NeutralBgDefault, 
  NeutralBorderWeakDefault, 
  PrimaryBgDefault, 
  NeutralTxtDefault, 
  NeutralTxtDisabled,
  PrimaryTxtWeakHover,
  PrimaryTxtInvDefault,
} from '../../tokens/js/light';

import { 
  PrimaryTxtWeakHover as PrimaryTxtWeakHoverDark,
  NeutralTxtDefault as NeutralTxtDefaultDark, 
  NeutralTxtDisabled as NeutralTxtDisabledDark,
} from '../../tokens/js/dark';

type Localization = {
  text: string
  href?: string
}

type TranslationsDropdownProps = {
  localizations: Localization[]
  location: string
  id: string
}

const TranslationsDropdown:React.FC<TranslationsDropdownProps> = ({ localizations, location, id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useTheme() as UpsunTheme;

  const containerStyles = css`
    position: relative;

    &:hover > ul,
    &:focus-within > ul { // see also: dropdownStyles
      display: flex !important;
    }

    &:hover > div,
    &:focus-within > div { // see also: dropdownLabelStyles
      color: ${theme.name === "light" ? PrimaryTxtWeakHover : PrimaryTxtWeakHoverDark};
    }

    &:hover > svg,
    &:focus-within > svg { // see also: chevronStyles
      transform: rotate(-180deg) !important;
    }
  `;

  const dropdownStyles = css`
    display: ${isOpen ? 'flex' : 'none'};
    padding: 0;
    flex-direction: column;
    list-style: none;
    text-transform: capitalize;

    ${mediaQueries.xl} {
      position: absolute;
      top: 100%;
      left: 0;
      min-width: ${pxToRem(256)};
      margin-top: ${pxToRem(8)};
      padding: ${pxToRem(12)};
      gap: ${pxToRem(12)};
      border-radius: ${pxToRem(8)};
      border: 1px solid ${NeutralBorderWeakDefault};
      background: ${NeutralBgDefault};
      box-shadow: ${dropShadows.small};
      z-index: ${z("modal")};

      &::before {
        content: '';
        position: absolute;
        width: 100%;
        left: 0;
        top: -${pxToRem(12)};
        height: ${pxToRem(20)};
      }
    }

    li {
      padding: 0;
      width: 100%;
    }

    li + li {
      margin-top: 0;
    }

    a,
    span {
      display: block;
      padding: ${pxToRem(10)} ${pxToRem(12)};

      ${mediaQueries.xl} {
        padding: ${pxToRem(4)};
        border-radius: ${pxToRem(4)};
        font-size: ${pxToRem(16)};
        line-height: 1.5;
        font-weight: ${weights.medium};
      }
    }

    a {
      ${mediaQueries.xl} {
        color: ${NeutralTxtDefault};
      }

      &:hover {
        color: ${theme.name === "light" ? PrimaryTxtWeakHover : PrimaryTxtWeakHoverDark};
        background: transparent;

        ${mediaQueries.xl} {
          background: ${PrimaryBgDefault};
          color: ${PrimaryTxtInvDefault};
        }
      }
    }

    span {
      color: ${theme.name === "light" ? NeutralTxtDisabled : NeutralTxtDisabledDark};

      ${mediaQueries.xl} {
        color: ${ NeutralTxtDisabled};
      }
    }
  `;

  const dropdownLabelStyles = css`
    display: flex;
    align-items: center;
    gap: ${pxToRem(4)};
    text-transform: capitalize;
    color: ${isOpen ? 
      (theme.name === "light" ? PrimaryTxtWeakHover : PrimaryTxtWeakHoverDark) :
      (theme.name === "light" ? NeutralTxtDefault : NeutralTxtDefaultDark)};
    cursor: pointer;

    &:hover {
      color: ${theme.name === "light" ? PrimaryTxtWeakHover : PrimaryTxtWeakHoverDark};
    }
    background: none;
    border: none;
    padding: 0;
    font-family: ${fonts.secondary};
    ${fontSizeLineHeight(13, 24)}
    letter-spacing: 0.03em;
    color: ${theme.name === "light"
        ? NeutralTxtDefault
        : NeutralTxtDefaultDark};
  `;

  const chevronStyles = css`
    transform: rotate(${isOpen ? "0deg" : "-180deg"});
    transition: ${transition()};

    path {
      fill: currentColor;
    }
  `;
  
  const currentLocale = localizations.find(localization => localization.href === location)?.text;

  return (
    <li css={containerStyles}>
      {/* @todo FFS make this accessible*/}
      <button 
        onClick={() => {setIsOpen(!isOpen)}} 
        css={dropdownLabelStyles}
        aria-expanded={isOpen}
        aria-controls={id}
      >
        {currentLocale}<ChevronUp css={chevronStyles} />
      </button>
      <ul css={dropdownStyles} id={id}>
        {localizations.map((localization, index) => {
          return (
            <li key={`localization--${localization.text}-${index}`}>
              {localization.href ? 
                <a href={localization.href}>{localization.text}</a> : 
                <span>{localization.text}</span>
              }
            </li>
          )
        })}
      </ul>
    </li>
  )
};

export default TranslationsDropdown;
